// src/types/tileBuilderTypes.ts
import { LeaderLayoutConfig } from "../components/Admin/TileBuilder/TilePreview/tilePreviewTypes";
import { TileDisplayContext } from "../support/tileDisplayContextUtils";
import { TileRuntimeContext } from "../support/tileRuntimeContext";

// --- Nivo-specific Type Definitions (based on Nivo documentation) ---
export type NivoLineCurve =
  | 'linear'
  | 'basis'
  | 'cardinal'
  | 'catmullRom'
  | 'monotoneX'
  | 'monotoneY'
  | 'natural'
  | 'step'
  | 'stepAfter'
  | 'stepBefore';

export type NivoScaleType = 'linear' | 'point' | 'time';
export type NivoSliceOption = 'x' | 'y' | false;
export type NivoBarGroupMode = 'stacked' | 'grouped';
export type NivoBarLayout = 'vertical' | 'horizontal';
export type NivoLegendAnchor =
  | 'top'
  | 'top-right'
  | 'right'
  | 'bottom-right'
  | 'bottom'
  | 'bottom-left'
  | 'left'
  | 'top-left'
  | 'center';
export type NivoLegendDirection = 'column' | 'row';
export type NivoLabelPosition = 'start' | 'middle' | 'end';
export type NivoMotionConfig =
  | 'default'
  | 'gentle'
  | 'wobbly'
  | 'stiff'
  | 'slow'
  | 'molasses'
  | string;

export interface MarginSettings {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

// --- TileSettings Interface ---
export interface TileSettings {
  // --- Data Mapping (from TilePreview) ---
  labelField?: string;
  valueField?: string;
  customLabel?: string;
  selectedColumns?: string[];

  // --- Margins (flat / legacy active values) ---
  marginTop?: number;
  marginRight?: number;
  marginBottom?: number;
  marginLeft?: number;
  chartMargin?: number;
  enableEditRect?: boolean;

  // --- Contextual Margin Presets (new) ---
  dashboardBaseMargins?: MarginSettings;
  dashboardLegendMargins?: MarginSettings;
  leaderBaseMargins?: MarginSettings;
  leaderLegendMargins?: MarginSettings;

  // --- Legacy Margin Presets (keep for backward compatibility) ---
  baseMargins?: MarginSettings;
  legendMargins?: MarginSettings;

  // --- Legend ---
  legendEnabled?: boolean;
  show_legend?: boolean;
  legendDefault?: boolean;
  legendAnchor?: NivoLegendAnchor;
  legendDirection?: NivoLegendDirection;
  legendTranslateX?: number;
  legendTranslateY?: number;
  legendItemWidth?: number;
  legendItemHeight?: number;
  legendSymbolSize?: number;
  legendItemsSpacing?: number;
  barEnableLabel?: boolean;
  legendItemOpacity?: number;

  // --- Axis ---
  xAxisLabel?: string;
  yAxisLabel?: string;
  barItemTopLabel?: boolean;
  barItemRightLabel?: boolean;
  itemRightLabel?: boolean;
  legendItemDirection?: string;
  axisBottomLegend?: string;
  axisBottomLegendOffset?: number;
  axisBottomRotation?: number;
  axisLeftLegend?: string;
  axisLeftLegendOffset?: number;
  axisLeftRotation?: number;
  enableGridX?: boolean;
  enableGridY?: boolean;
  xFormat?: string;
  yFormat?: string;

  // --- Colors & Patterns ---
  colorScheme?: string;
  enablePattern?: boolean;
  patternType?: string;

  // --- Interactivity & Animation ---
  animate?: boolean;
  motionConfig?: NivoMotionConfig;
  isInteractive?: boolean;
  isFocusable?: boolean;
  tooltipMode?: 'auto' | 'labelValue' | 'seriesValue' | 'seriesLabelValue';
  tooltipLabelTitle?: string;
  tooltipSeriesTitle?: string;
  tooltipValueTitle?: string;
  tooltipShowLabel?: boolean;
  tooltipShowSeries?: boolean;
  tooltipShowValue?: boolean;

  // --- Bar Chart Specific ---
  groupMode?: NivoBarGroupMode;
  layout?: NivoBarLayout;
  padding?: number;
  innerPadding?: number;
  borderRadius?: number;
  borderWidth?: number;
  borderColor?: string;
  enableLabel?: boolean;
  labelSkipWidth?: number;
  labelSkipHeight?: number;
  labelPosition?: NivoLabelPosition;
  labelOffset?: number;
  labelTextColor?: string;
  enableTotals?: boolean;
  totalsOffset?: number;

  // --- Line Chart Specific ---
  lineCurve?: NivoLineCurve;
  xScaleType?: NivoScaleType;
  yScaleType?: NivoScaleType;
  yScaleMin?: 'auto' | number;
  yScaleMax?: 'auto' | number;
  yScaleStacked?: boolean;
  yScaleReverse?: boolean;
  enableSlices?: NivoSliceOption;
  enableArea?: boolean;
  areaOpacity?: number;
  areaBaselineValue?: number;
  enablePointLabel?: boolean;
  pointLabelYOffset?: number;
  pointSize?: number;
  pointBorderWidth?: number;
  pointBorderColor?: string;
  pointColor?: string;
  useCategorical?: boolean;
  useCustomColors?: boolean;

  // --- Pie/Donut/Gauge Specific ---
  innerRadius?: number;
  startAngle?: number;
  endAngle?: number;
  enableArcLinkLabels?: boolean;
  enableArcLabels?: boolean;

  // --- Generic / Existing ---
  boundarySize?: { width: number; height: number };

  truncateTickAt?: number;
  axisBottom?: object;
  axisLeft?: object;
  xScale?: object;
  yScale?: object;
  useMesh?: boolean;

  // --- Aggregation ---
  seriesField?: string;
  aggregate?: 'auto' | 'count' | 'sum' | 'avg' | 'min' | 'max';
  defaultSeriesName?: string;
  keys?: string[];

  // --- Value format ---
  valueFormat?:
    | 'number'
    | 'currency'
    | 'percent'
    | Intl.NumberFormatOptions
    | ((v: number) => string);
  currency?: string;

  // --- Scale ---
  valueScaleType?: 'linear' | 'symlog';
  valueScaleMin?: number | '';
  valueScaleMax?: number | '';
  valueScaleNice?: boolean;
  valueScaleRound?: boolean;
  valueScaleSymlogConstant?: number;

  // --- Colors ---
  colors?: string[];
  seriesColors?: Record<string, string>;

  // --- Date filter ---
  dateRangeOptions?: string[];
  defaultDateRange?: string;

  [key: string]: any;
}

// --- Query and Transformation interfaces ---
export interface Query {
  dataSource: string;
  model: string;
  entity?: string;
  function: string;
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  params?: { [key: string]: any };
  payload?: any;
  queryResult?: any;
  functionResult?: any;
  runtimeParams?: Record<string, any>;
  conditionalExecution?: {
    whenField: string;
    equals: any;
  };
  dependsOn?: number[];
  transformFunction?: string;
  useResultFrom?: number;
  resultMapping?: { [key: string]: string };
  localFilePath?: string;
  localFileName?: string;
  enableDateFilter?: boolean;
  /**
   * For `dataSource === 'File Import'`: the `ImportedDataset` id whose parsed data backs this tile,
   * and the id of the specific derived widget within that dataset to render. See
   * `src/support/dataImport/fileImportSource.ts`.
   */
  importedDatasetId?: number;
  widgetId?: string;
  /**
   * For `dataSource === 'Campaign Delivery'`: the `CampaignDelivery` id whose live rows back this
   * tile, and which of its related tables to flatten ('step_status' completion charts by default, or
   * 'target' per-customer status). Campaign-agnostic — the id names a delivery, never a campaign.
   * See `src/support/dataImport/campaignChartSource.ts`.
   */
  campaignDeliveryId?: number;
  campaignDeliveryDataset?: 'step_status' | 'target';
  /**
   * Builder-UI only: the campaign that owns `campaignDeliveryId`, persisted so the tile builder's
   * campaign->delivery pickers can repopulate when editing an existing tile. The render-time resolver
   * ignores it (it needs only the delivery id) — it is not campaign-specific coupling in a generic path.
   */
  campaignDeliveryCampaignId?: number;
}

export interface Transformation {
  id?: string;
  transformation: {
    type: string;
    config?: any;
    logic?: string;
  };
  queryIndices?: number[];
}

// --- TileBuilderState interface ---
export interface TileBuilderState {
  tileName: string;
  tileDescription: string;
  chartType: string;
  queries: Query[];
  transformations: Transformation[];
  settings: TileSettings;
  fields: any[];
}

export interface TilePreviewState {
  displayContext: TileDisplayContext;
  runtimeContext: TileRuntimeContext;
  selectedSourceIdx: number;
  leaderLayout?: LeaderLayoutConfig;
}

// --- Initial Settings ---
export const initialSettings: TileSettings = {
  // --- Data Mapping ---
  labelField: '',
  valueField: '',
  customLabel: '',
  selectedColumns: [],

  // --- Margins ---
  marginTop: 24,
  marginRight: 16,
  marginBottom: 48,
  marginLeft: 16,
  enableEditRect: false,

  // --- Contextual Margin Presets ---
  dashboardBaseMargins: { top: 24, right: 16, bottom: 48, left: 16 },
  dashboardLegendMargins: { top: 24, right: 16, bottom: 48, left: 16 },
  leaderBaseMargins: { top: 24, right: 16, bottom: 48, left: 16 },
  leaderLegendMargins: { top: 24, right: 16, bottom: 48, left: 16 },

  // --- Legacy Margin Presets ---
  baseMargins: { top: 24, right: 16, bottom: 48, left: 16 },
  legendMargins: { top: 24, right: 16, bottom: 48, left: 16 },

  // --- Legend ---
  show_legend: false,
  legendAnchor: 'bottom',
  legendDirection: 'row',
  legendTranslateX: 0,
  legendTranslateY: 0,
  legendItemWidth: 104,
  legendItemHeight: 20,
  legendSymbolSize: 18,
  legendItemsSpacing: 5,

  // --- Axis ---
  xAxisLabel: '',
  yAxisLabel: '',
  axisBottomLegend: '',
  axisBottomLegendOffset: 40,
  axisBottomRotation: 0,
  axisLeftLegend: '',
  axisLeftLegendOffset: -55,
  axisLeftRotation: 0,
  enableGridX: false,
  enableGridY: true,

  // --- Colors & Patterns ---
  colorScheme: 'nivo',
  enablePattern: false,
  patternType: 'lines',

  // --- Interactivity & Animation ---
  animate: true,
  motionConfig: 'default',
  isInteractive: true,
  isFocusable: false,

  // --- Bar Chart Specific ---
  groupMode: 'stacked',
  layout: 'vertical',
  padding: 0.1,
  innerPadding: 0,
  borderRadius: 0,
  borderWidth: 0,
  borderColor: 'inherit',
  enableLabel: true,
  labelSkipWidth: 0,
  labelSkipHeight: 0,
  labelPosition: 'middle',
  labelOffset: 0,
  labelTextColor: 'theme',
  enableTotals: false,
  totalsOffset: 10,

  // --- Line Chart Specific ---
  lineCurve: 'linear',
  xScaleType: 'point',
  yScaleType: 'linear',
  yScaleMin: 'auto',
  yScaleMax: 'auto',
  yScaleStacked: false,
  yScaleReverse: false,
  enableSlices: 'x',
  enableArea: false,
  areaOpacity: 0.35,
  areaBaselineValue: 0,
  enablePointLabel: false,
  pointLabelYOffset: 0,
  pointSize: 6,
  pointBorderWidth: 1,
  pointBorderColor: '#fff',
  pointColor: '',
  truncateTickAt: 0,

  // --- Pie/Donut/Gauge Specific ---
  innerRadius: 0.7,
  startAngle: -90,
  endAngle: 90,
  enableArcLinkLabels: false,
  enableArcLabels: false,

  aggregate: 'auto',
  defaultSeriesName: 'Value',

  valueScaleType: 'linear',
  valueScaleNice: true,
  valueScaleRound: false,

  useCategorical: true,
  useCustomColors: false,
  colors: [],
};

// --- Initial TileBuilderState ---
export const initialTileState: TileBuilderState = {
  tileName: '',
  tileDescription: '',
  chartType: '',
  queries: [],
  transformations: [],
  settings: {
    ...initialSettings,
    chartMargin: 10,
    legendDefault: false,
    barEnableLabel: false,
    labelSkipHeight: 30,
    barItemTopLabel: false,
    barItemRightLabel: false,
    axisBottomLegend: '',
    axisLeftLegend: '',
    axisBottomLegendOffset: 40,
    axisLeftLegendOffset: -55,
    legendAnchor: 'left',
    legendDirection: 'column',
    legendTranslateX: 0,
    legendTranslateY: 0,
    legendItemWidth: 104,
    legendItemHeight: 20,
    legendItemDirection: 'left-to-right',
    legendItemOpacity: 0.85,
    legendSymbolSize: 18,
    enablePattern: false,
    patternType: '',
    lineCurve: 'linear',
    enableArea: false,
    areaOpacity: 0.35,
    areaBaselineValue: 0,
    enableSlices: false,
    xScaleType: 'linear',
    yScaleType: 'linear',
    yScaleMin: 'auto',
    yScaleMax: 'auto',
    yScaleStacked: false,
    yScaleReverse: false,
    xFormat: '',
    yFormat: '',
    animate: true,
    isInteractive: true,
    isFocusable: true,
    enablePointLabel: false,
    pointLabelYOffset: 0,
    pointSize: 6,
    pointBorderWidth: 1,
    pointBorderColor: '#fff',
    pointColor: '',
    marginTop: 20,
    marginRight: 20,
    marginBottom: 60,
    marginLeft: 50,
    colorScheme: '',
    show_legend: false,

    dashboardBaseMargins: { top: 20, right: 20, bottom: 60, left: 50 },
    dashboardLegendMargins: { top: 20, right: 20, bottom: 60, left: 50 },
    leaderBaseMargins: { top: 20, right: 20, bottom: 60, left: 50 },
    leaderLegendMargins: { top: 20, right: 20, bottom: 60, left: 50 },

    baseMargins: { top: 20, right: 20, bottom: 60, left: 50 },
    legendMargins: { top: 20, right: 20, bottom: 60, left: 50 },
  },
  fields: [],
};

export const initialPreviewState: TilePreviewState = {
  displayContext: 'preview',
  runtimeContext: {},
  selectedSourceIdx: 0,
  leaderLayout: undefined,
};